import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export interface User {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role?: string;
  created_at?: string;
  is_active?: boolean;
  signature?: string;
  mobile_number?: string;
  service_description?: string;
  date_of_birth?: string;
  picture?: string;
}

export interface CreateUserDto {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  organization_id: string;
  role?: string;
  signature?: string;
  mobile_number?: string;
  service_description?: string;
  date_of_birth?: string;
  picture?: string;
}

export interface AdminUserReportRow {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role?: string;
  mobile_number?: string;
  is_active?: boolean;
  invitation_pending?: boolean;
  created_at?: string;
  organization_id?: string;
  organization_name?: string;
  organization_email?: string;
  last_gift_sent_on?: string | null;
  gifts_sent?: number;
  subscription?: AdminUserReportSubscription | null;
}

export interface AdminUserReportSubscription {
  plan_name: string;
  plan_amount: string;
  status: string;
  subscription_type?: string | null;
  trial_days?: number;
  started_on?: string | null;
  ended_on?: string | null;
  last_charged_on?: string | null;
}

export interface UpdateUserDto {
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  role?: string;
  signature?: string;
  mobile_number?: string;
  service_description?: string;
  date_of_birth?: string;
  picture?: string;
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["User", "OrganizationUsers"],
  endpoints: (builder) => ({
    getUsersByOrganization: builder.query<Response<User[]>, string>({
      query: (organizationId) => ({
        url: `user/organization/${organizationId}`,
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: User[] } | User[];
        if (Array.isArray(r)) {
          return { success: true, code: 200, message: "", data: r };
        }
        if (r && typeof r === "object" && "data" in r && Array.isArray(r.data)) {
          return { success: true, code: 200, message: "", data: r.data };
        }
        return { success: true, code: 200, message: "", data: [] };
      },
      providesTags: (_result, _error, orgId) => [
        { type: "OrganizationUsers", id: orgId },
      ],
    }),
    getAdminUserReport: builder.query<Response<AdminUserReportRow[]>, void>({
      query: () => ({
        url: "admin/reports/users",
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const extract = (value: unknown): AdminUserReportRow[] | null => {
          if (Array.isArray(value)) return value as AdminUserReportRow[];
          if (value && typeof value === "object" && "data" in value) {
            return extract((value as { data: unknown }).data);
          }
          return null;
        };
        return {
          success: true,
          code: 200,
          message: "",
          data: extract(response) || [],
        };
      },
      providesTags: ["User"],
    }),
    createUser: builder.mutation<Response<unknown>, CreateUserDto>({
      query: (data) => ({
        url: "user/create",
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { organization_id }) => [
        { type: "OrganizationUsers", id: organization_id },
      ],
    }),
    updateUser: builder.mutation<
      Response<User>,
      { userId: string; data: UpdateUserDto; organizationId: string }
    >({
      query: ({ userId, data }) => ({
        url: `user/${userId}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { organizationId }) => [
        { type: "OrganizationUsers", id: organizationId },
      ],
    }),
  }),
});

export const {
  useGetUsersByOrganizationQuery,
  useGetAdminUserReportQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
} = userApi;
